import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2, Users, Lock, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function GroupFeedPage() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, p] = await Promise.all([
        api.get(`/private-groups/${groupId}`),
        api.get(`/private-groups/${groupId}/posts`),
      ]);
      setGroup(g.data);
      setPosts(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load group.");
    } finally { setLoading(false); }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/private-groups/${groupId}/posts`, { text: text.trim() });
      setText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post."); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  }
  if (!group) {
    return (
      <div className="p-6">
        <Link to="/private-groups" className="text-rose-500 text-sm">← Back to groups</Link>
        <div className="mt-4 text-zinc-400">Group not found.</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="group-feed-root">
      <Link to="/private-groups" className="inline-flex items-center gap-1.5 text-rose-500 hover:text-rose-400 text-sm mb-4">
        <ArrowLeft size={14} /> All groups
      </Link>
      <header className="mb-5">
        <div className="text-xs uppercase tracking-[0.3em] text-violet-400 mb-1 flex items-center gap-1.5">
          <Lock size={11} /> Private
        </div>
        <h1 className="font-display text-3xl md:text-4xl">{group.name}</h1>
        {group.description && <p className="text-sm text-zinc-400 mt-1">{group.description}</p>}
        <div className="mt-3 flex items-center gap-1 text-xs text-zinc-500">
          <Users size={12} /> {group.member_count} member{group.member_count === 1 ? "" : "s"}
          <div className="flex -space-x-2 ml-2">
            {group.members.slice(0, 6).map((m) => (
              <Avatar key={m.id} className={`w-6 h-6 border border-black`}>
                <AvatarImage src={m.avatar_url} />
                <AvatarFallback className="bg-rose-600 text-white text-[9px]">{m.display_name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
            ))}
            {group.member_count > 6 && (
              <span className="text-[10px] text-zinc-400 ml-3">+{group.member_count - 6}</span>
            )}
          </div>
        </div>
      </header>

      {/* Composer */}
      <div className="card-surface p-3 mb-4" data-testid="group-composer">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 2000))}
          placeholder={`Say something to @${group.name.toLowerCase().replace(/\s+/g, "-")}…`}
          rows={2}
          className="bg-transparent border-0 focus-visible:ring-0 resize-none"
          data-testid="group-post-input"
        />
        <div className="flex items-center justify-between mt-2 border-t border-white/5 pt-2">
          <span className="text-[10px] text-zinc-500">{text.length}/2000 · members only</span>
          <Button onClick={send} disabled={busy || !text.trim()} className="btn-crimson rounded-full h-8 px-4" data-testid="group-post-submit">
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />} Post
          </Button>
        </div>
      </div>

      {/* Feed */}
      {posts.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">
          No posts yet. Start the conversation.
        </div>
      ) : (
        <div className="space-y-3" data-testid="group-posts">
          {posts.map((p) => (
            <div key={p.id} className="card-surface p-4" data-testid={`group-post-${p.id}`}>
              <div className="flex gap-3">
                <Link to={`/u/${p.author?.id}`}>
                  <Avatar className={`w-10 h-10 border border-white/10`}>
                    <AvatarImage src={p.author?.avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-white text-sm">
                      {p.author?.display_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Link to={`/u/${p.author?.id}`} className={`text-sm font-medium hover:text-rose-500`}>
                      {p.author?.display_name}
                    </Link>
                    <span className="text-xs text-zinc-500">· {timeAgo(p.created_at)}</span>
                  </div>
                  <div className="text-sm text-white/90 mt-1 whitespace-pre-wrap leading-relaxed">{p.text}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
